import { MutationCache, QueryClient } from '@tanstack/react-query'

import { extractApiError } from './error'
import { toast } from './toast'

/**
 * Default mutation error handler. Fires only when the caller did NOT supply
 * its own `onError` - that way inline-error UIs (dialogs that show the message
 * next to their submit button, RHF setError flows, etc.) keep working
 * unchanged, while the silent-catch and forgot-to-handle cases get a toast.
 *
 * Mutations that legitimately want to stay silent (optimistic micro-updates,
 * fire-and-forget telemetry) can pass `meta: { hideToast: true }`.
 */
export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      if (mutation.options.onError) return
      if (mutation.meta?.hideToast === true) return
      toast.error(extractApiError(error).detail)
    },
  }),
  defaultOptions: {
    queries: {
      // This app owns its own offline behaviour: reads fall back to the
      // IndexedDB mirror inside the queryFn (networkFirstWithCache), and
      // writes queue to the outbox inside the axios interceptor. React
      // Query's default networkMode ('online') sat ABOVE both layers and
      // paused the fn whenever navigator.onLine was false - the queryFn
      // never ran, so the Dexie fallback never served, and a mutation
      // never reached axios, so nothing was ever queued: the New Booking
      // modal just spun forever on a genuinely offline device. 'always'
      // hands the network decision back to the layers built for it.
      networkMode: 'always',
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status === 401 || status === 403 || status === 404) return false
        return failureCount < 2
      },
      // The realtime socket is the fast path for cross-tab updates; focus
      // refetch is the reconcile path underneath it. Without it, one missed
      // or never-sent frame leaves a background tab stale for the full
      // staleTime with no recovery short of a reload. staleTime still
      // debounces: focusing only refetches queries older than 5 minutes.
      refetchOnWindowFocus: true,
    },
    // See queries.networkMode above - same reasoning, worse symptom.
    mutations: { retry: false, networkMode: 'always' },
  },
})

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: { hideToast?: boolean }
  }
}
